import { Store } from 'vuex';
import { Router } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import { GET_TICKET_UNREAD } from '@/store/action_types';
import { TICKETS_ROUTE } from '@/router/routes';
import { TICKET_UNREAD_POLL_MS, TOAST_GLOBAL_CONFIG } from '@/utils/const';
import { ticketAssignedNotice, ticketMoveNotice } from '@/utils/ticket_lifecycle';
import { personName } from '@/utils/ticket';
import { TicketUnread } from '@/dtos/ticket.dto';

/**
 * Keeps the count of tickets with unread messages — the badge on Tickets in
 * the navs — current: asked of the API when the app opens, every
 * TICKET_UNREAD_POLL_MS while the tab is in view, when the tab comes back
 * into view, and whenever the app knows the count changed (a reply sent, a
 * conversation read — see refreshTicketUnread).
 *
 * What the answer brings that is new since the last time is announced with a
 * toast that opens the ticket: to an organization, the moves staff made on its
 * tickets ("Your ticket … is in progress"); to staff, the tickets a colleague
 * gave them. What was already there when the app opened is not news.
 *
 * Started by App.vue once someone is signed in; stopped when they sign out.
 * The same module lives in the admin and in both organization apps.
 */

// How long a toast stays: long enough to read and click.
const NOTICE_DURATION_MS = 8000;
// At most this many toasts at once; the badge says the rest.
const NOTICES_AT_ONCE = 3;

let store: Store<any> | null = null;
let router: Router | null = null;
let timer: number | null = null;
// What has been seen already (announced, or there when the app opened).
let seen: Set<string> | null = null;

const toast = useToast();

const canAsk = (): boolean =>
  Boolean(store && store.getters.sessionToken && store.getters.account?.data) &&
  (typeof document === 'undefined' || document.visibilityState !== 'hidden');

const openTicket = (ticketId: string) => {
  if (!router) return;
  router.push({ name: TICKETS_ROUTE, query: { ticket: ticketId } }).catch(() => undefined);
};

/** Toasts what the answer brings that is new — but not about the ticket open on screen. */
const announce = (unread: TicketUnread | null) => {
  if (!unread) return;
  const notices = [
    ...(unread.statusChanges || []).map((change) => ({
      key: `move:${change.id}`,
      ticketId: change.ticketId,
      message: ticketMoveNotice(change.type, change.subject),
    })),
    ...(unread.assignments || []).map((assignment) => ({
      key: `assigned:${assignment.ticketId}:${assignment.assignedAt}`,
      ticketId: assignment.ticketId,
      message: ticketAssignedNotice(personName(assignment.assignedBy, 'A colleague'), assignment.subject, assignment.organizationName),
    })),
  ];

  if (!seen) {
    seen = new Set(notices.map((notice) => notice.key));
    return;
  }

  const openId = store?.getters.ticketComments?.ticket?.id;
  const fresh = notices.filter((notice) => !seen!.has(notice.key));
  fresh.forEach((notice) => seen!.add(notice.key));
  fresh
    .filter((notice) => notice.ticketId !== openId)
    .slice(0, NOTICES_AT_ONCE)
    .forEach((notice) => {
      toast.info(notice.message, {
        ...TOAST_GLOBAL_CONFIG,
        duration: NOTICE_DURATION_MS,
        onClick: () => openTicket(notice.ticketId),
      } as any);
    });
};

/** Asks now — after something that changes what is unread. */
export const refreshTicketUnread = (): void => {
  if (!canAsk()) return;
  store!.dispatch(GET_TICKET_UNREAD).then(announce).catch(() => undefined);
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') refreshTicketUnread();
};

/** `appRouter` lets a toast open the ticket it is about. */
export const startTicketUnreadPolling = (appStore: Store<any>, appRouter?: Router | null): void => {
  if (store) return;
  store = appStore;
  router = appRouter || null;
  seen = null;
  refreshTicketUnread();
  timer = window.setInterval(refreshTicketUnread, TICKET_UNREAD_POLL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', refreshTicketUnread);
};

export const stopTicketUnreadPolling = (): void => {
  if (timer !== null) window.clearInterval(timer);
  timer = null;
  if (store) {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', refreshTicketUnread);
  }
  store = null;
  router = null;
  seen = null;
};
